/* Waggle Enrich — search.js
   Predictive search in the search overlay (brief §5.6): 180ms debounce, /search/suggest with
   section_id=predictive-search, combobox pattern (aria-expanded, aria-controls, aria-activedescendant),
   arrow keys move through results, Enter opens, Escape collapses then closes. */

const W = window.Waggle;
const doc = document;
const input = doc.getElementById('SearchOverlayInput');
const results = doc.getElementById('PredictiveResults');
const status = doc.getElementById('PredictiveStatus');

doc.addEventListener('overlay:open', (event) => {
  if (event.detail.id !== 'SearchOverlay' || !input) return;
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });
});

if (input && results && W.settings.predictiveSearch) {
  let timer = null;
  let controller = null;
  let active = -1;

  const options = () => W.qsa('[role="option"]', results);

  const collapse = () => {
    results.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-controls');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };

  const select = (index) => {
    const list = options();
    if (!list.length) return;
    active = (index + list.length) % list.length;
    list.forEach((option, i) => option.setAttribute('aria-selected', String(i === active)));
    const current = list[active];
    input.setAttribute('aria-activedescendant', current.id);
    current.scrollIntoView({ block: 'nearest' });
  };

  const fetchResults = async (terms) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product,collection,page',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${W.routes.predictiveSearch}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.predictive__results');
      if (!fresh) {
        collapse();
        return;
      }
      /* JS guard: never show a product row without a photo. */
      W.qsa('.predictive__item--product', fresh).forEach((option) => {
        if (!option.querySelector('img')) option.closest('li')?.remove();
      });
      results.replaceChildren(fresh);
      const list = options();
      input.setAttribute('aria-expanded', list.length ? 'true' : 'false');
      if (list.length) input.setAttribute('aria-controls', 'PredictiveListbox');
      input.removeAttribute('aria-activedescendant');
      active = -1;
      const total = fresh.querySelector('[data-predictive-total]');
      if (status) status.textContent = W.strings.suggestionsStatus.replace('[count]', total ? total.dataset.predictiveTotal : list.length);
      W.qsa('.predictive__group > li', fresh)
        .slice(0, 10)
        .forEach((item, index) => {
          W.animate(item, [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], {
            duration: 420,
            delay: Math.min(index, 6) * 40,
            easing: W.ease.out,
            fill: 'backwards',
          });
        });
    } catch (error) {
      if (error.name !== 'AbortError') collapse();
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const terms = input.value.trim();
    if (!terms) {
      if (controller) controller.abort();
      collapse();
      return;
    }
    timer = setTimeout(() => fetchResults(terms), 180);
  });

  input.addEventListener('keydown', (event) => {
    const list = options();
    if (event.key === 'Escape' && input.getAttribute('aria-expanded') === 'true') {
      event.preventDefault();
      event.stopPropagation();
      collapse();
      return;
    }
    if (!list.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      select(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      select(active - 1);
    } else if (event.key === 'Enter' && active > -1) {
      event.preventDefault();
      window.location.href = list[active].href;
    }
  });

  results.addEventListener('pointermove', (event) => {
    const option = event.target.closest('[role="option"]');
    if (!option) return;
    const index = options().indexOf(option);
    if (index !== active) select(index);
  });

  doc.addEventListener('overlay:close', (event) => {
    if (event.detail.id === 'SearchOverlay') collapse();
  });
}
